import React, { useEffect, useState } from 'react'
import { scroller } from 'react-scroll'
import {
  BlockingLoader,
  Button,
  Link,
  Container,
  ListContainer,
  ScrollArea,
  Popup,
  TextBody,
  TextTitle,
  ToastContext,
  Tools
} from '@pdrj/pmi-iqos-ds-react'
import {
  getToastMessagesFromResponse,
  getToastMessagesFromError,
  extractGigyaErrorCode,
  parseTranslations,
  PublishedComponent,
  CommonTranslations,
  setTranslations,
  htmlTranslation,
  PinTypes,
  LayoutContainer,
  ProfileAddressForm,
  ProfileMarketingSettings,
  ProfileReadOnlyFormData,
  ProfileUpdateDataActions,
  ProfileFormEmail,
  ProfileFormPhone,
  TwoFAStep1,
  TwoFAStep2,
  Sites,
  cookie
} from '@pdrj/pmi-iqos-shared-react'
import { IqosphereApiManager } from '@iqosphere-common/services'
import ProfileAccountFormsTranslations from './profile-account-forms.json'

const updateProfileErrorsMappings = {
  114: 'tk_updateProfileError114',
  117: 'tk_updateProfileError117',
  141: 'tk_updateProfileError141',
  142: 'tk_updateProfileError142',
  121: 'tk_updateProfileError121',
  112: 'tk_updateProfileError112',
  126: 'tk_validatePhonePinError126',
  129: 'tk_validatePhonePinError129'
}

const ProfileAccountFormsComponent = ({ line, translations, logError }) => {
  const api = new IqosphereApiManager(line)

  const fieldNames = {
    firstName: 'FirstName',
    lastName: 'LastName',
    dob: 'Birthday',
    email: 'EmailAddress',
    phone: 'TelephoneNumber',
    addresses: 'Addresses',
    city: 'AddressLine01',
    street: 'AddressLine02',
    postalCode: 'PostalCode',
    prefecture: 'Area',
    emailPin: 'emailAddressPin',
    phonePin: 'telephoneNumberPin',
    OptInDirectMailCommunications: 'OptInDirectMailCommunications',
    OptInEmailCommunications: 'OptInEmailCommunications',
    OptInPhoneCommunications: 'OptInPhoneCommunications'
  }
  const communicationRef = ScrollArea.getRef()
  const [loading, setLoading] = useState(true)
  const [profile, setProfile] = useState({
    [fieldNames.firstName]: '',
    [fieldNames.lastName]: '',
    [fieldNames.dob]: '',
    [fieldNames.email]: '',
    [fieldNames.phone]: '',
    [fieldNames.OptInDirectMailCommunications]: false,
    [fieldNames.OptInEmailCommunications]: false,
    [fieldNames.OptInPhoneCommunications]: false
  })
  const [updatedProfile, setUpdatedProfile] = useState(profile)
  const [toggleEditAddress, setToggleEditAddress] = useState(false)
  const [address, setAddress] = useState({
    Type: 1,
    Country: 'JP',
    [fieldNames.city]: '',
    [fieldNames.street]: '',
    [fieldNames.postalCode]: '',
    [fieldNames.prefecture]: ''
  })
  const [updatedAddress, setUpdatedAddress] = useState(address)
  const [formAddressIsValid, setFormAddressIsValid] = useState(false)
  const [savingAddress, setSavingAddress] = useState(false)
  const [toggleEditEmail, setToggleEditEmail] = useState(false)
  const [toggleEditPhone, setToggleEditPhone] = useState(false)
  const [emailFormIsValid, setEmailFormIsValid] = useState(false)
  const [phoneFormIsValid, setPhoneFormIsValid] = useState(false)
  const [savingEmail, setSavingEmail] = useState(false)
  const [savingPhone, setSavingPhone] = useState(false)
  const [isEmailPINModalOpen, setIsEmailPINModalOpen] = useState(false)
  const [isPhonePINModalOpen, setIsPhonePINModalOpen] = useState(false)
  const [phonePinSent, setPhonePinSent] = useState(false)
  const [lastUsedPhonePinMethod, setLastUsedPhonePinMethod] = useState(
    PinTypes.TEXT
  )
  const [isAV, setIsAV] = useState(false)
  const [toggleEditCommunicationOptions, setToggleEditCommunicationOptions] =
    useState(false)
  const [savingCommunicationOptions, setSavingCommunicationOptions] =
    useState(false)

  const popupToast = ToastContext.getContextActions().popupToast
  const emailPhoneSection = 'email-phone-section'

  if (translations) {
    setTranslations(translations)
  }

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const tokenInfo = cookie.getTokenInfo()
      if (tokenInfo && tokenInfo.isAV) {
        setIsAV(true)
      }

      const res = await api.iqosphereProfile.getProfile()

      if (res.Birthday && res.Birthday.length === 8) {
        const year = res.Birthday.slice(0, 4)
        const month = res.Birthday.slice(4, 6)
        const day = res.Birthday.slice(6, 8)
        res.Birthday = `${year}-${month}-${day}`
      }
      const { Addresses, ...Profile } = res
      if (!Profile.settings) {
        Profile.settings = {}
      }
      const mainAddress = Addresses.filter(key => key.Type === 1)[0] || address
      setProfile(Profile)
      setUpdatedProfile(Profile)
      setAddress(mainAddress)
      setUpdatedAddress(mainAddress)
    } catch (err) {
      logError(err)
    } finally {
      setLoading(false)
    }
  }

  const getProfileFullname = profile => {
    return profile[fieldNames.lastName] + ' ' + profile[fieldNames.firstName]
  }

  const updateProfile = async newData => {
    try {
      setLoading(true)
      const { data } = await api.profile.updateProfile({ data: newData })
      setAddress(updatedAddress)
      setProfile(updatedProfile)
      setToggleEditAddress(false)
      setToggleEditEmail(false)
      setToggleEditPhone(false)
      setIsEmailPINModalOpen(false)
      setIsPhonePINModalOpen(false)
      setToggleEditCommunicationOptions(false)
      popupToast({
        content: getToastMessagesFromResponse(data)
      })
    } catch (err) {
      if (!isEmailPINModalOpen && !isPhonePINModalOpen) {
        setUpdatedProfile(profile)
      }
      let toastTitle = getToastMessagesFromError(
        err,
        translations.tk_unableToUpdateProfile
      )
      if (err.data && Array.isArray(err.data) && err.data.length > 0) {
        const gigyaErrorCode = extractGigyaErrorCode(err.data[0].message)
        if (gigyaErrorCode) {
          // is gigya error
          const gigyaTranslationLabel =
            translations['tk_gigya_err_' + gigyaErrorCode]
          if (gigyaTranslationLabel && gigyaTranslationLabel !== 'TBD') {
            toastTitle = translations['tk_gigya_err_' + gigyaErrorCode]
          }
        } else {
          const errorCode = err.data[0] && err.data[0].code
          toastTitle = translations[updateProfileErrorsMappings[errorCode]]
            ? translations[updateProfileErrorsMappings[errorCode]]
            : translations.tk_unableToUpdateProfile
        }
      }
      popupToast({
        type: ToastContext.types.error,
        title: toastTitle
      })
    } finally {
      setLoading(false)
      setSavingAddress(false)
      setSavingEmail(false)
      setSavingPhone(false)
      setSavingCommunicationOptions(false)
    }
  }

  const handleChangeInput = e => {
    const name = e.target.name
    const value = e.target.value
    switch (name) {
      case fieldNames.street:
        setUpdatedAddress({
          ...updatedAddress,
          [fieldNames.street]: value
        })
        break
      case fieldNames.city:
        setUpdatedAddress({
          ...updatedAddress,
          [fieldNames.city]: value
        })
        break
      case fieldNames.prefecture:
        setUpdatedAddress({
          ...updatedAddress,
          [fieldNames.prefecture]: value
        })
        break
      case fieldNames.email:
        setUpdatedProfile({
          ...updatedProfile,
          [fieldNames.email]: value
        })
        break
      case fieldNames.phone:
        setUpdatedProfile({
          ...updatedProfile,
          [fieldNames.phone]: value
        })
        break
      case fieldNames.OptInDirectMailCommunications:
        setUpdatedProfile({
          ...updatedProfile,
          OptInDirectMailCommunications:
            !updatedProfile.OptInDirectMailCommunications
        })
        break
      case fieldNames.OptInEmailCommunications:
        setUpdatedProfile({
          ...updatedProfile,
          OptInEmailCommunications: !updatedProfile.OptInEmailCommunications
        })
        break
      case fieldNames.OptInPhoneCommunications:
        setUpdatedProfile({
          ...updatedProfile,
          OptInPhoneCommunications: !updatedProfile.OptInPhoneCommunications
        })
        break
      default:
    }
  }

  const handleChangeZipCode = selectedOption => {
    if (selectedOption && selectedOption.item) {
      setUpdatedAddress({
        ...updatedAddress,
        [fieldNames.city]:
          selectedOption.item.city + ' ' + selectedOption.item.village,
        [fieldNames.postalCode]: selectedOption.value,
        [fieldNames.prefecture]: selectedOption.item.state
      })
    }
  }

  const handleEditAddress = () => {
    setToggleEditAddress(!toggleEditAddress)
    setUpdatedAddress(address)
    setToggleEditEmail(false)
    setToggleEditPhone(false)
    setToggleEditCommunicationOptions(false)
  }

  const handleSaveAddress = async () => {
    setSavingAddress(true)
    const newProfile = {
      [fieldNames.addresses]:
        Object.keys(updatedAddress).length > 0 ? [updatedAddress] : []
    }
    updateProfile(newProfile)
  }

  const handleEditEmail = () => {
    setToggleEditAddress(false)
    setToggleEditCommunicationOptions(false)
    setToggleEditEmail(!toggleEditEmail)
    setUpdatedProfile(profile)
    setUpdatedAddress(address)
  }

  const handleSaveEmail = () => {
    setSavingEmail(true)
    handleRequestEmailPin()
  }

  const handleRequestEmailPin = async () => {
    try {
      setLoading(true)
      await api.auth.requestEmailVerificationPIN(
        updatedProfile.EmailAddress,
        true
      )
      setIsEmailPINModalOpen(true)
    } catch (err) {
      const errorCode = err && err.data[0] && err.data[0].code
      const toastTitle =
        errorCode && translations[updateProfileErrorsMappings[errorCode]]
          ? translations[updateProfileErrorsMappings[errorCode]]
          : getToastMessagesFromError(
              err,
              translations.tk_twoFAErrorRequestEmailPin
            )

      popupToast({
        type: ToastContext.types.error,
        title: toastTitle
      })
    } finally {
      setLoading(false)
      setSavingEmail(false)
    }
  }

  const scrollToContainer = () => {
    scroller.scrollTo(emailPhoneSection, {
      duration: 500,
      smooth: true
    })
  }

  const handleUpdateEmail = pin => {
    const newProfile = {
      [fieldNames.email]: updatedProfile[fieldNames.email],
      [fieldNames.emailPin]: pin
    }
    updateProfile(newProfile)
    scrollToContainer()
  }

  const handleEditPhone = () => {
    setToggleEditAddress(false)
    setToggleEditCommunicationOptions(false)
    setToggleEditPhone(!toggleEditPhone)
    setUpdatedProfile(profile)
    setUpdatedAddress(address)
  }

  const handleSavePhone = () => {
    setSavingPhone(true)
    setPhonePinSent(false)
    setIsPhonePINModalOpen(true)
  }

  const handleRequestPhonePin = async pinType => {
    let requestFunc = null
    let errorLabel = null
    switch (pinType) {
      case PinTypes.TEXT:
        requestFunc = api.auth.requestSMSVerificationPIN
        errorLabel = translations.tk_twoFAErrorRequestPhonePinText
        break
      case PinTypes.VOICE:
      default:
        requestFunc = api.auth.requestVoiceVerificationPIN
        errorLabel = translations.tk_twoFAErrorRequestPhonePinCall
        break
    }
    try {
      setLastUsedPhonePinMethod(pinType)
      setLoading(true)
      await requestFunc(updatedProfile.TelephoneNumber, true)
      setPhonePinSent(true)
    } catch (err) {
      const errorCode = err && err.data[0] && err.data[0].code
      const toastTitle = translations[updateProfileErrorsMappings[errorCode]]
        ? translations[updateProfileErrorsMappings[errorCode]]
        : getToastMessagesFromError(err, errorLabel)
      popupToast({
        type: ToastContext.types.error,
        title: toastTitle
      })
    } finally {
      setLoading(false)
      setSavingPhone(false)
    }
  }

  const handleUpdatePhone = pin => {
    const newProfile = {
      [fieldNames.phone]: updatedProfile[fieldNames.phone],
      [fieldNames.phonePin]: pin
    }
    updateProfile(newProfile)
    scrollToContainer()
  }

  const handleCloseVerificationPopup = () => {
    setIsEmailPINModalOpen(false)
    setIsPhonePINModalOpen(false)
    setToggleEditEmail(false)
    setToggleEditPhone(false)
    setSavingEmail(false)
    setSavingPhone(false)
    setToggleEditCommunicationOptions(false)
    setUpdatedProfile(profile)
  }

  const handleEditCommunicationOptions = () => {
    const toggle = !toggleEditCommunicationOptions
    setIsEmailPINModalOpen(false)
    setIsPhonePINModalOpen(false)
    setToggleEditEmail(false)
    setToggleEditPhone(false)
    setSavingEmail(false)
    setSavingPhone(false)
    setToggleEditAddress(false)
    setToggleEditCommunicationOptions(toggle)
    setUpdatedProfile(profile)
    setUpdatedAddress(address)
  }

  const handleSaveCommunicationOptions = () => {
    setSavingCommunicationOptions(true)
    const newProfile = {
      OptInDirectMailCommunications:
        updatedProfile.OptInDirectMailCommunications,
      OptInEmailCommunications: updatedProfile.OptInEmailCommunications,
      OptInPhoneCommunications: updatedProfile.OptInPhoneCommunications || false
    }
    updateProfile(newProfile)
  }

  const renderUpdateData = (
    cancelHandler,
    saveHandler,
    isLoading,
    isDisabled
  ) => {
    return (
      <Container
        display={Container.displays.flex}
        alignItems={Container.alignItemsValues.center}>
        <Container pr="8" display={Container.displays.flex}>
          <Link
            onClickHandler={cancelHandler}
            data-testing="Profile_Link_CancelUpdate">
            {translations.tk_cancelButton}
          </Link>
        </Container>
        <Button
          onClickHandler={saveHandler}
          text={translations.tk_saveButton}
          loading={isLoading}
          disabled={isDisabled}
          data-testing="Profile_Button_Save"
        />
      </Container>
    )
  }

  return (
    <Container position={Container.positions.relative}>
      {loading && <BlockingLoader loading />}
      <LayoutContainer xMainLayout={!Tools.isDesktop()}>
        <Container divider={Container.dividers.bottom} py="6">
          <TextBody
            size={TextBody.sizes.s}
            {...htmlTranslation(
              translations,
              'tk_registeredAccountDescription'
            )}
          />
        </Container>
        <Container divider={Container.dividers.bottom} py="6">
          <Container
            pb="6"
            display={Container.displays.flex}
            flexDirection={[
              Container.flexDirections.column,
              Container.flexDirections.row
            ]}
            justifyContent={Container.justifyContentValues.between}>
            <TextTitle heading={TextTitle.headings.h4}>
              {translations.tk_userInfoTitle}
            </TextTitle>
          </Container>
          <ListContainer columns={1} xBetween="4" yBetween="6">
            <ProfileReadOnlyFormData
              label={translations.tk_fullName}
              value={getProfileFullname(profile)}
              data-testing-prefix="ProfileAccountForms"
              data-testing-suffix="FullName"
              labelWidth={Tools.isDesktop() ? '1/4' : '1/2'}
              valueWidth={Tools.isDesktop() ? '3/4' : '1/2'}
            />
            <ProfileReadOnlyFormData
              label={translations.tk_dob}
              value={profile[fieldNames.dob]}
              data-testing-prefix="ProfileAccountForms"
              data-testing-suffix="Birthday"
              labelWidth={Tools.isDesktop() ? '1/4' : '1/2'}
              valueWidth={Tools.isDesktop() ? '3/4' : '1/2'}
            />
          </ListContainer>
        </Container>
        <Container divider={Container.dividers.bottom} py="6">
          <Container
            pb="6"
            display={Container.displays.flex}
            flexDirection={[
              Container.flexDirections.column,
              Container.flexDirections.row
            ]}
            justifyContent={Container.justifyContentValues.between}>
            <TextTitle heading={TextTitle.headings.h4}>
              {translations.tk_prizeDeliveryAddressTitle}
            </TextTitle>

            {isAV && (
              <Container
                pt={['6', '0']}
                display={Container.displays.flex}
                justifyContent={Container.justifyContentValues.end}>
                <ProfileUpdateDataActions
                  translations={translations}
                  toggleEdit={toggleEditAddress}
                  editHandler={handleEditAddress}
                  saveHandler={handleSaveAddress}
                  isDisabled={!formAddressIsValid}
                  isLoading={savingAddress}
                  data-testing-prefix={'ProfileAccountForms_PrizeDelivery'}
                />
              </Container>
            )}
          </Container>
          <ProfileAddressForm
            translations={translations}
            formFieldNames={fieldNames}
            updatedFormData={updatedAddress}
            handleChangeInput={handleChangeInput}
            handleChangeZipCode={handleChangeZipCode}
            handleFormIsValid={setFormAddressIsValid}
            toggleEdit={toggleEditAddress}
            isSavingData={savingAddress}
            site={Sites.iqosphere}
            line={line}
            data-testing-prefix={'ProfileAccountForms_PrizeDelivery'}
          />
        </Container>
        <Container
          divider={Container.dividers.bottom}
          id={emailPhoneSection}
          py="6">
          <Container
            pb="6"
            display={Container.displays.flex}
            flexDirection={[
              Container.flexDirections.column,
              Container.flexDirections.row
            ]}
            justifyContent={Container.justifyContentValues.between}>
            <TextTitle heading={TextTitle.headings.h4}>
              {translations.tk_emailPhoneTitle}
            </TextTitle>
            {(toggleEditEmail || toggleEditPhone) && (
              <Container
                pt={['6', '0']}
                display={Container.displays.flex}
                justifyContent={Container.justifyContentValues.end}>
                <ProfileUpdateDataActions
                  translations={translations}
                  toggleEdit={toggleEditEmail || toggleEditPhone}
                  editHandler={
                    toggleEditEmail ? handleEditEmail : handleEditPhone
                  }
                  saveHandler={
                    toggleEditEmail ? handleSaveEmail : handleSavePhone
                  }
                  isDisabled={
                    (toggleEditEmail &&
                      (updatedProfile[fieldNames.email].toLowerCase() ===
                        profile[fieldNames.email].toLowerCase() ||
                        !emailFormIsValid)) ||
                    (toggleEditPhone &&
                      (updatedProfile[fieldNames.phone] ===
                        profile[fieldNames.phone] ||
                        !phoneFormIsValid))
                  }
                  isLoading={savingEmail || savingPhone}
                  data-testing-prefix={'ProfileAccountForms_EmailPhone'}
                />
              </Container>
            )}
          </Container>
          <ListContainer columns={1} xBetween="4" yBetween="6">
            <ProfileFormEmail
              translations={translations}
              updatedProfile={updatedProfile}
              formFieldNames={fieldNames}
              toggleEdit={toggleEditEmail}
              isEditionDisabled={savingEmail}
              isToggleEditDisabled={
                isAV ? savingEmail || toggleEditPhone : true
              }
              handleChangeInput={handleChangeInput}
              handleEdit={handleEditEmail}
              setFormIsValid={setEmailFormIsValid}
              data-testing-preix={'ProfileAccountForms'}
            />
            <ProfileFormPhone
              translations={translations}
              updatedProfile={updatedProfile}
              formFieldNames={fieldNames}
              toggleEdit={toggleEditPhone}
              isEditionDisabled={savingPhone}
              isToggleEditDisabled={
                isAV ? savingPhone || toggleEditEmail : true
              }
              handleChangeInput={handleChangeInput}
              handleEdit={handleEditPhone}
              setFormIsValid={setPhoneFormIsValid}
              data-testing-preix={'ProfileAccountForms'}
            />
          </ListContainer>
        </Container>
        <ProfileMarketingSettings
          translations={translations}
          toggleEditCommunicationOptions={toggleEditCommunicationOptions}
          handleEditCommunicationOptions={handleEditCommunicationOptions}
          handleSaveCommunicationOptions={handleSaveCommunicationOptions}
          savingCommunicationOptions={savingCommunicationOptions}
          communicationBlockRef={communicationRef}
          renderUpdateData={renderUpdateData}
          handleChangeInput={handleChangeInput}
          updatedProfile={updatedProfile}
          showPhoneOptIn
        />
      </LayoutContainer>
      <Popup
        isOpen={isEmailPINModalOpen || isPhonePINModalOpen}
        onCloseHandler={handleCloseVerificationPopup}
        data-testing-prefix="ProfileAccountForms">
        {loading && <BlockingLoader loading={true} />}
        <Container
          fullWidth
          display={Container.displays.flex}
          flexDirection={Container.flexDirections.column}
          justifyContent={Container.justifyContentValues.center}>
          <TextTitle heading={TextTitle.headings.h4}>
            {isEmailPINModalOpen
              ? translations.tk_emailVerificationTitle
              : translations.tk_phoneVerificationTitle}
          </TextTitle>
          {isEmailPINModalOpen && (
            <TwoFAStep1
              translations={translations}
              isOpen={isEmailPINModalOpen}
              email={updatedProfile.EmailAddress}
              emailPinSent={true}
              loading={loading}
              handleRequestEmailPin={handleRequestEmailPin}
              handleVerifyEmailPin={handleUpdateEmail}
            />
          )}
          {isPhonePINModalOpen && (
            <TwoFAStep2
              translations={translations}
              isOpen={isPhonePINModalOpen}
              phone={updatedProfile.TelephoneNumber}
              phonePinSent={phonePinSent}
              loading={loading}
              handleRequestPhonePin={handleRequestPhonePin}
              handleVerifyPhonePin={handleUpdatePhone}
              defaultPhonePinMethod={lastUsedPhonePinMethod}
            />
          )}
        </Container>
      </Popup>
    </Container>
  )
}

const ProfileAccountForms = props => {
  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={ProfileAccountFormsComponent}
        {...ComponentDefaultProps}
        {...props}
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...ProfileAccountFormsTranslations,
  ...CommonTranslations
}

const ComponentDefaultProps = {
  componentName: 'ProfileAccountForms',
  translations: {
    ...parseTranslations(ComponentTranslations, true)
  }
}

ProfileAccountForms.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    line: !!req?.query?.line,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: async () => {}
  })
}

export default ProfileAccountForms
